import { computed, FunctionalComponent, StyleValue } from "vue";
import { isDarkColor } from "../../../../../skyward.base.color";
import { ButtonDefinition } from "../../../../../types";
import { Key } from "../../../../../types/skyward/svg";
import Button from "../../../../core/button";
import SkyButton from "../../../../core/button";
import Svg from "../../../../core/svg";
import { ErrorStates, State } from "../../../core/store/types";
import { DashboardItemFeatures, DashboardItemStoreState, DashboardItemTypes, TileFeatures, TileParameters } from "../../store/types";
import { VueDashboardItemClasses, VueDashboardItemTileClasses } from "./../../classes";

interface ErrorBodyProps
{
    state: State<unknown, unknown, unknown>;
}

const TROUBLE_LOADING_MESSAGE = "We're having trouble loading this content";
const RELOAD_MESSAGE = "There could be something happening on our end. Please click Reload to try again.";

export const ErrorBody: FunctionalComponent<ErrorBodyProps> = ({ state }) =>
{
    const isSmallWidth = (state.features as unknown as DashboardItemFeatures)?.settings?.width < 2;
    const isSmallHeight = (state.features as unknown as DashboardItemFeatures)?.settings?.height < 2;
    return <div
        class={{
            [VueDashboardItemClasses.ErrorBody]: true,
            [VueDashboardItemClasses.ErrorBodySmallHeight]: isSmallHeight,
            [VueDashboardItemClasses.ErrorBodySmallWidth]: isSmallWidth,
        }}>
        <p class={VueDashboardItemClasses.ErrorHeader}>{TROUBLE_LOADING_MESSAGE}</p>
        {!isSmallWidth && !isSmallHeight &&
            <p>{RELOAD_MESSAGE}</p>
        }
        <SkyButton
            class="roundedTwoColor primary"
            svgName="Update"
            style="display: flex; align-items: center"
            onClick={(e) =>
            {
                e.target!.dispatchEvent(new CustomEvent("refresh", { bubbles: true }));
            }}
        >Reload</SkyButton>
    </div>
};

interface SecurityErrorBodyProps
{
    message?: string
};

export const SecurityErrorBody: FunctionalComponent<SecurityErrorBodyProps> = (props) =>
{
    return <div class={{ [VueDashboardItemClasses.SecurityErrorBody]: true }} {...props} >
        <Svg name="Warning"
            tabindex="-1"
            svgClasses={["svgNoHover", "vueDashboard__securityErrorIcon"]}
            style="height: 4.8rem; width: 4.8rem; margin-bottom: 0.8rem;"
        />
        <p class={VueDashboardItemClasses.SecurityErrorBodyText}>Unable to load Content</p>
        {
            props.message && <p class={VueDashboardItemClasses.SecurityErrorBodyText}>{props.message}</p>
        }
    </div>
};

export function renderErrorBody(state: State<unknown, unknown, unknown>)
{
    const stateFeatures = state.features as unknown as DashboardItemFeatures;

    switch (state.errorState)
    {
        case ErrorStates.OK: return null;
        case ErrorStates.ServerError:
            if (stateFeatures?.settings?.typeCode == DashboardItemTypes.Live || stateFeatures?.settings?.typeCode == DashboardItemTypes.Section)
            {
                const error: DashboardItemErrorMessage =
                {
                    Type: DashboardItemErrorMessageType.General,
                    Message: TROUBLE_LOADING_MESSAGE,
                    Details: RELOAD_MESSAGE
                };
                return <TileErrorContent error={error}
                    useRefreshAction={true}
                    width={stateFeatures.settings.width}
                    height={stateFeatures.settings.height}
                    iconColor={stateFeatures.settings.parameters.iconColor as string}
                    backgroundColor={stateFeatures.settings.parameters.backgroundColor as string} />;
            }
            return <ErrorBody state={state} />;
        case ErrorStates.NotSecure:
        case ErrorStates.FatalNotSecure:
            if (stateFeatures?.settings?.typeCode == DashboardItemTypes.Live || stateFeatures?.settings?.typeCode == DashboardItemTypes.Section)
            {
                const error: DashboardItemErrorMessage =
                {
                    Type: DashboardItemErrorMessageType.Security,
                    Message: "Access denied",
                };
                return <TileErrorContent error={error}
                    width={stateFeatures.settings.width}
                    height={stateFeatures.settings.height}
                    iconColor={stateFeatures.settings.parameters?.iconColor as string}
                    backgroundColor={stateFeatures.settings.parameters?.backgroundColor as string} />;
            }
            return <SecurityErrorBody message={state.errorUserMessage} />;
        default:
            throw new Error("not implemented");
    }
}

export enum DashboardItemErrorMessageType
{
    General = 0,
    Security = 1
};

export interface DashboardItemErrorMessage
{
    Type: DashboardItemErrorMessageType;
    Message: string;
    Details?: string;
    Recommendations?: string[] | null;
};

interface ContentErrorProps
{
    error: DashboardItemErrorMessage,
    height: number,
    width: number,
    useRefreshAction?: boolean,
    iconColor?: string,
    backgroundColor?: string
};

export const TileErrorContent: FunctionalComponent<ContentErrorProps> = (props) =>
{
    const iconStyle: StyleValue = {};

    if (props.iconColor && props.iconColor.length > 0)
    {
        iconStyle.fill = props.iconColor;
    }

    const containerStyle: StyleValue = {};
    let isDarkBackground = true;

    if (props.backgroundColor)
    {
        isDarkBackground = isDarkColor(props.backgroundColor);
        const containerBackgroundColor = isDarkBackground
            ? "rgba(255, 255, 255, 0.10)" // Light color for dark background
            : "rgba(0, 0, 0, 0.10)";
        containerStyle["background-color"] = containerBackgroundColor;
    }

    if (props.height === 1 && props.error.Details)
    {
        iconStyle.display = "none";
    }

    const buttonText = computed(() =>
    {
        if (props.useRefreshAction)
        {
            return "Reload";
        }

        if (props.width > 1)
        {
            return "More Information";
        }

        return "Information";
    });

    let svgIcon: Key;

    switch (props.error.Type)
    {
        case DashboardItemErrorMessageType.Security:
            svgIcon = "Error";
            break;
        case DashboardItemErrorMessageType.General:
        default:
            svgIcon = "Warning";
            break;
    }

    return <div
        class={{
            [VueDashboardItemTileClasses.ErrorContent]: true,
            [VueDashboardItemTileClasses.ErrorContentRefresh]: !!props.useRefreshAction,
            [VueDashboardItemTileClasses.ErrorContentWide]: props.width > 1,
            [VueDashboardItemTileClasses.ErrorContentTall]: props.height > 1,
            [VueDashboardItemTileClasses.ErrorContentLight]: !isDarkBackground,
        }}
        style={containerStyle}
        {...props} >
        {!props.useRefreshAction &&
            <Svg name={svgIcon}
                tabindex="-1"
                svgClasses={["svgNoHover", VueDashboardItemTileClasses.Svg, VueDashboardItemTileClasses.ErrorContentSvg]}
                style={iconStyle}
            />
        }
        { renderContent() }
        { renderActionButton() }
    </div>

    function renderContent()
    {
        return (<>
            <p title={!props.useRefreshAction && props.height === 1 ? props.error.Message : undefined}
                    class={{
                    [VueDashboardItemTileClasses.ErrorContentText]: true,
                    [VueDashboardItemTileClasses.ErrorContentTextRefresh]: !!props.useRefreshAction,
                    [VueDashboardItemTileClasses.ErrorContentTextRefreshHeader]: !!props.useRefreshAction,
                }}
                >
                    { props.error.Message }
                </p>
                {props.useRefreshAction && props.width > 1 && props.height > 1 &&
                    <p class={{
                        [VueDashboardItemTileClasses.ErrorContentText]: true,
                        [VueDashboardItemTileClasses.ErrorContentTextRefresh]: true
                    }}>{props.error.Details}</p>
                }
            </>);
    }

    function renderActionButton()
    {
        if (props.useRefreshAction)
        {
            return <Button svgName="Update"
                class={{
                    [VueDashboardItemClasses.ErrorActionButton]: true,
                    [VueDashboardItemClasses.ErrorActionButtonTall]: props.height > 1,
                }}
                onClick={(e) =>
                {
                    e.target!.dispatchEvent(new CustomEvent("refresh", { bubbles: true }));
                }}>{buttonText.value}</Button>;
        }

        if (props.error.Details)
        {
            return <Button svgName="Info"
                class={{
                    [VueDashboardItemClasses.ErrorActionButton]: true,
                    [VueDashboardItemClasses.ErrorActionButtonTall]: props.height > 1,
                }}
                onClick={(e) => moreInformationDialog.apply(e, [props.error.Details!, props.error.Recommendations])}>{buttonText.value}</Button>;
        }

        return null;
    }
};

function moreInformationDialog(this: MouseEvent, details: string, recommendations?: string[] | null)
{
    this.stopPropagation();

    const closeButton: ButtonDefinition =
    {
        text: "Close",
        icon: "Close",
        click: function ()
        {
            dialog!.close();
        }
    }

    const content = $("<div/>")
    const message = $("<div/>",
        {
            class: VueDashboardItemClasses.DashboardItemInformationText
        }).text(details);
    message.appendTo(content);
    $("<br>").appendTo(content);

    if (recommendations && recommendations.length > 0)
    {
        const recommendationsHeader = $("<p/>", { class: "bold" }).text("RECOMMENDATIONS:");
        recommendationsHeader.appendTo(content);
        $("<br>").appendTo(content);
        const recommendationsList = $("<ul/>",
            {
                class: VueDashboardItemClasses.DashboardItemInformationUnorderedList,
            });

        recommendations.forEach((recommendation) =>
        {
            const recommendationItem = $("<li/>",
                {
                    class: VueDashboardItemClasses.DashboardItemInformationText
                }).text(recommendation);
            recommendationItem.appendTo(recommendationsList);
        });

        recommendationsList.appendTo(content);
    }

    const dialog = window.skyward.modalLayer.showDialog(
        "More Information",
        content,
        [closeButton]
    );
}